function statusToIota(s) {
	switch (s) {
		default:
			return 0
		case "processed":
			return 1
		case "confirmed":
			return 2
		case "finalized":
			return 3
		case "timeout":
			return 4
	}
}

export default {
	namespaced: true,
	state: () => ({
		tradeUserChannel: new BroadcastChannel("trade:user"),
		signatureStatus: new BroadcastChannel("signature:status"),
		lastCheck: 0,
		activeSignatures: {},
		activeSignatureCount: 0,
		lastRefresh: 0,
		autoRemoveTimeouts: {}
	}),
	mutations: {
		add_signature(state, {sig, status}) {
			// Create a copy of the current activeSignatures
			const updatedSignatures = {...state.activeSignatures};

			// Add the new signature
			updatedSignatures[sig] = {
				status: status,
				createdAt: new Date()
			};

			// Enforce max 5 most recent
			const entries = Object.entries(updatedSignatures);
			if (entries.length > 5) {
				entries.sort((a, b) => new Date(a[1].createdAt) - new Date(b[1].createdAt));
				const [oldestSig] = entries[0];
				delete updatedSignatures[oldestSig];
				// Update the count once at the end
				state.activeSignatureCount = entries.length - 1;
			} else {
				// Increment count if we didn't remove any
				state.activeSignatureCount++;
			}

			// Update the state with all changes at once
			state.activeSignatures = updatedSignatures;
		},

		update_status(state, {sig, status, pendingSig = null}) {
			if (!status)
				return;


			if (!state.activeSignatures[sig])
				state.tradeUserChannel.postMessage({signature: sig}) //Only send when not already added

			// console.log("signatures::update_status", sig, {status, pendingSig})
			if (!pendingSig && statusToIota(state.activeSignatures[sig]?.status?.confirmationStatus) > statusToIota(status?.confirmationStatus))
				return

			if (pendingSig) {
				// console.log("signatures::clear_pending", sig, pendingSig)
				delete state.activeSignatures[pendingSig]
				state.activeSignatures[sig] = Object.assign(state.activeSignatures[sig] || {createdAt: new Date(), status: status})
			}

			state.activeSignatures[sig] = Object.assign(state.activeSignatures[sig] || {}, status)
			state.activeSignatureCount++
			state.tradeUserChannel.postMessage({signature: sig})

			// Schedule auto-removal for transactions with a result
			if (status?.confirmationStatus === "confirmed" ||
				status?.confirmationStatus === "finalized" ||
				status?.confirmationStatus === "timeout" ||
				status?.err) {

				// Clear any existing timeout for this signature
				if (state.autoRemoveTimeouts[sig]) {
					clearTimeout(state.autoRemoveTimeouts[sig])
				}

				// Set a new timeout to remove this transaction after 15 seconds
				state.autoRemoveTimeouts[sig] = setTimeout(() => {
					// Use a function to access the current state
					// We can't use the state parameter directly as it would be stale
					import('@/store').then(storeModule => {
						const store = storeModule.default
						if (store) {
							store.commit("signatures/clear_status", sig)
							// The deletion of the timeout will happen in the clear_status mutation
						}
					})
				}, 15000) // 15 seconds
			}
		},
		clear_status(state, sig) {
			// console.log("signatures::clear_status", sig)
			// Clear any existing timeout for this signature
			if (state.autoRemoveTimeouts[sig]) {
				clearTimeout(state.autoRemoveTimeouts[sig])
				delete state.autoRemoveTimeouts[sig]
			}
			delete state.activeSignatures[sig]
			state.activeSignatureCount--
		},
		clear_all(state) {
			// console.log("signatures::clear_all")
			// Clear all timeouts
			Object.keys(state.autoRemoveTimeouts).forEach(sig => {
				clearTimeout(state.autoRemoveTimeouts[sig])
			})
			state.autoRemoveTimeouts = {}
			state.activeSignatures = {};
			state.lastCheck = 0;
			state.activeSignatureCount = 0
		}
	},
	actions: {
		//Race condition with the clear_all
		async refresh({state, commit, rootState}) {
			const now = Date.now();
			if (now - state.lastRefresh < 2_000) {
				return;
			}
			state.lastRefresh = now;

			const sigs = []
			const keys = Object.keys(state.activeSignatures)
			for (let i = 0; i < keys.length; i++) {
				if (keys[i].length < 32) continue //Pending sigs

				const si = state.activeSignatures[keys[i]]
				// Skip signatures that already have a result (confirmed, finalized, timeout, or error)
				if (si?.confirmationStatus === "confirmed" ||
					si?.confirmationStatus === "finalized" ||
					si?.confirmationStatus === "timeout" ||
					si?.err) {
					continue
				}

				// Skip signatures that have confirmations > 0
				if (si?.confirmations > 0)
					continue

				sigs.push(keys[i])
			}
			if (!sigs.length)
				return

			// console.log("signatures::refresh fetching:", sigs, sigs.length)
			const status = await rootState.solana.client.getSignatureStatuses(sigs, {searchTransactionHistory: false}).catch(err => {
				console.error("Error fetching signature statuses:", err);
				
				// Mark transactions as error state manually when the API call fails
				sigs.forEach(sig => {
					if (state.activeSignatures[sig]) {
						// Set generic error for transactions that failed
						commit("update_status", {
							sig, 
							status: {
								confirmationStatus: "error", 
								err: "Error"
							}
						});
						state.signatureStatus.postMessage({
							sig, 
							status: {
								confirmationStatus: "error", 
								err: "Error"
							}
						});
					}
				});
				
				return;
			})
			if (Object.keys(state.activeSignatures).length === 0)
				return //Cleared between the 2 checks

			status?.value?.forEach((s, i) => {
				const age = new Date() - state.activeSignatures[sigs[i]]?.createdAt;
				// console.log(`Signature ${sigs[i]} age: ${age}ms`, JSON.stringify(state.activeSignatures[sigs[i]])); // Add logging

				// Check for timeout
				if (new Date() - state.activeSignatures[sigs[i]]?.createdAt > 60_000) {
					commit("update_status", {sig: sigs[i], status: {confirmationStatus: "timeout", err: "Timeout"}})
					state.signatureStatus.postMessage({sig: sigs[i], status: {confirmationStatus: "timeout", err: "Timeout"}})
					return
				}

				// Update status for transactions that are still pending
				commit("update_status", {sig: sigs[i], status: s})
				state.signatureStatus.postMessage({sig: sigs[i], status: s})
			})
		}
	}
}
